import { ActivationRuleModel } from './activations';
import { TaskModel } from './tasks';
import { VersionedEntityOperationType, VersionedEntityType } from './versions';

enum PathType {
  USER_DEFINED = 'USER_DEFINED',
  /** @deprecated Removed by the API; only USER_DEFINED is returned. Kept so legacy data during rollout renders harmlessly. */
  START = 'START',
  /** @deprecated Removed by the API; only USER_DEFINED is returned. Kept so legacy data during rollout renders harmlessly. */
  END = 'END'
}

enum NodeType {
  TASK = 'TASK',
  MESSAGE_TEMPLATE = 'MESSAGE_TEMPLATE',
  ACTION_MOVE_TO_PATH = 'ACTION_MOVE_TO_PATH',
  GROUP = 'GROUP'
}

enum PublishConfirmationMode {
  SINGLE_TASK = 'SINGLE_TASK',
  ALL_TASKS = 'ALL_TASKS'
}

interface CreateNodesBatchModel {
  type?: NodeType | null;

  // parent
  pathEntityId?: string | null;

  // children
  // MESSAGE_TEMPLATE
  messageTemplates?: string[] | null;

  // ACTION_MOVE_TO_PATH
  actionMoveToPathEntityIds?: string[] | null;

  // TASK
  isRequired?: boolean | null;
  taskEntityIds?: string[] | null;

  groupEntityId?: string | null;
}

interface CreateOrUpdateStrategyNodeModel {
  type?: NodeType | null;

  // parent & ordering
  pathEntityId?: string | null;
  nextEntityId?: string | null;

  // activation
  activationRuleEntityId?: string | null;

  // children
  // MESSAGE_TEMPLATE
  messageTemplate?: string | null;

  // ACTION_MOVE_TO_PATH
  actionMoveToPathEntityId?: string | null;

  // TASK
  isRequired?: boolean | null;
  taskEntityId?: string | null;

  // GROUP
  groupEntityId?: string | null;
}

interface CreateOrUpdateStrategyPathModel {
  activationRuleEntityId?: string | null;
  isMultiActivatable?: boolean;
  nextEntityId?: string | null;
}

interface BasePathModel {
  // version & identity
  entityId: string | null;
  versionType: VersionedEntityType;
  operationType: VersionedEntityOperationType;
  createdAt: string;
  updatedAt: string;

  // own fields
  type?: PathType;
  activationRuleEntityId: string | null;
  activationRule: ActivationRuleModel | null;
  isMultiActivatable: boolean;
  nextEntityId: string | null;
}

interface NodeModel {
  // version & identity
  entityId: string | null;
  versionType: VersionedEntityType;
  operationType: VersionedEntityOperationType;
  createdAt: string;
  updatedAt: string;

  // own fields
  type: NodeType;
  pathEntityId: string;
  nextEntityId: string | null;
  activationRuleEntityId: string | null;
  activationRule: ActivationRuleModel | null;

  // children
  // MESSAGE_TEMPLATE
  messageTemplate: string | null;

  // ACTION_MOVE_TO_PATH
  actionMoveToPathEntityId: string | null;
  actionMoveToPath: BasePathModel | null;

  // TASK
  isRequired: boolean;
  taskEntityId: string | null;
  task: TaskModel | null;

  // GROUP
  groupEntityId: string | null;
  groupNodes: NodeModel[] | null;
}

function createEmptyNode(): NodeModel {
  return {
    entityId: null,
    versionType: VersionedEntityType.DRAFT,
    operationType: VersionedEntityOperationType.ADDED,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),

    type: NodeType.TASK,
    pathEntityId: null,
    nextEntityId: null,
    activationRuleEntityId: null,
    activationRule: null,

    messageTemplate: null,
    actionMoveToPathEntityId: null,
    actionMoveToPath: null,
    isRequired: true,
    taskEntityId: null,
    task: null,
    groupEntityId: null,
    groupNodes: null
  };
}

interface PathModel extends BasePathModel {
  // children
  nodes: NodeModel[];
}

interface StrategyModel {
  // version & identity
  entityId: string;
  createdAt: string;
  updatedAt: string;

  // children
  paths: PathModel[];
}

export { createEmptyNode, NodeType, PathType, PublishConfirmationMode };
export type {
  CreateNodesBatchModel,
  CreateOrUpdateStrategyNodeModel,
  CreateOrUpdateStrategyPathModel,
  NodeModel,
  PathModel,
  StrategyModel
};
